import React, {useCallback} from 'react';
import {Typography} from '@customink/pigment-react';

import './Welcome_Lg.scss';
import {useAuth} from '~/auth/AuthContext';
import {useDispatch, useSelector} from 'react-redux';
import {DesignUnsavedDialog} from '../AccountsMenu/DesignUnsavedDialog';
import {updateLabExitWarningEligibility} from '../../actions/ConfigActions';
import {pushGtmEvent} from '../../utils/analytics';
import {getCheckoutSummaryLink} from '~/constants/CheckoutLink';
import {isDirty} from '../../selectors/CanvasSelectors';

interface RetrieveDesignsButtonProps {
  onRetrieveDesigns: (event: React.MouseEvent<HTMLDivElement>) => void;
}
const RetrieveDesignsButton = (props: RetrieveDesignsButtonProps) => {
  return (
    <div className="ndx-Welcome_Lg-button" onClick={props.onRetrieveDesigns}>
      <div className="ndx-Welcome_Lg-buttonImage welcomeRetrieveDesignImage" />
      <Typography variant="uiTextSmall" className="ndx-Welcome_Lg-buttonText">
        Retrieve a
        <br />
        Saved Design
      </Typography>
    </div>
  );
};

interface ReviewCartButtonProps {
  onCheckout: () => void;
}

const ReviewCartButton = (props: ReviewCartButtonProps) => {
  return (
    <div className="ndx-Welcome_Lg-button" onClick={props.onCheckout}>
      <div className="ndx-Welcome_Lg-buttonImage welcomeCheckoutImage" />
      <Typography variant="uiTextSmall" className="ndx-Welcome_Lg-buttonText">
        Review Cart &
        <br />
        Check Out
      </Typography>
    </div>
  );
};

interface StartButtonsProps {
  cartItemCount: number;
  customDimension: string;
  isMultiItemEligible: boolean;
  isWaysToGetStartedTest?: boolean;
  onSaveFormRedirect: (
    event: React.MouseEvent<HTMLButtonElement>,
    customDimension: string
  ) => void;
  onAddArt: (
    event: React.MouseEvent<HTMLDivElement>,
    customDimension: string
  ) => void;
  onAddText: (
    event: React.MouseEvent<HTMLDivElement>,
    customDimension: string
  ) => void;
  onProduct: (
    event: React.MouseEvent<HTMLDivElement>,
    customDimension: string
  ) => void;
  onRetrieveDesigns: (
    event: React.MouseEvent<HTMLDivElement>,
    customDimension: string
  ) => void;
  onTextStart: (data: {source: string}) => void;
  onUpload: (
    event: React.MouseEvent<HTMLDivElement>,
    customDimension: string
  ) => void;
}

const StartButtons = (props: StartButtonsProps) => {
  const auth = useAuth();
  const dispatch = useDispatch();
  const {cartItemCount, customDimension, isMultiItemEligible} = props;
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const isDesignDirty = useSelector(isDirty);

  const handleCheckout = useCallback(() => {
    window.location.href = getCheckoutSummaryLink();
  }, []);

  const onCheckoutConfirmation = useCallback(() => {
    if (isDesignDirty) {
      setDialogOpen(true);
      dispatch(updateLabExitWarningEligibility(false));
      pushGtmEvent({
        event: 'lab_interaction',
        interaction_category: 'save design alert click',
        interaction_action: 'open'
      });
      return;
    }
    pushGtmEvent({
      event: 'lab_interaction',
      interaction_category: 'save design alert click',
      interaction_action: 'continue without saving'
    });
    handleCheckout();
  }, [isDesignDirty]);

  const handleClose = useCallback(() => {
    dispatch(updateLabExitWarningEligibility(true));
    setDialogOpen(false);
  }, []);
  const handleCloseDialog = useCallback(() => {
    pushGtmEvent({
      event: 'lab_interaction',
      interaction_category: 'save design alert click',
      interaction_action: 'close'
    });
    handleClose();
  }, []);
  const handleSubmit = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      pushGtmEvent({
        event: 'lab_interaction',
        interaction_category: 'save design alert click',
        interaction_action: 'save my design'
      });
      props.onSaveFormRedirect(event, customDimension);
      handleClose();
    },
    []
  );
  const handleDismiss = useCallback(() => {
    pushGtmEvent({
      event: 'lab_interaction',
      interaction_category: 'save design alert click',
      interaction_action: 'continue without saving'
    });
    handleClose();
    handleCheckout();
  }, []);
  const onAddText = (event: React.MouseEvent<HTMLDivElement>) => {
    props.onAddText(event, customDimension);
    props.onTextStart({
      source: 'wb'
    });
  };
  const onUpload = (event: React.MouseEvent<HTMLDivElement>) => {
    props.onUpload(event, customDimension);
  };

  const onAddArt = (event: React.MouseEvent<HTMLDivElement>) => {
    props.onAddArt(event, customDimension);
  };

  const onProduct = (event: React.MouseEvent<HTMLDivElement>) => {
    props.onProduct(event, customDimension);
  };

  const onRetrieveDesigns = (event: React.MouseEvent<HTMLDivElement>) => {
    props.onRetrieveDesigns(event, customDimension);
  };

  return (
    <div className="ndx-Welcome_Lg-buttons">
      <div className="ndx-Flex-Row">
        <div className="ndx-Welcome_Lg-button" onClick={onUpload}>
          <div className="ndx-Welcome_Lg-buttonImage welcomeUploadCTAImage" />
          <Typography
            variant="uiTextSmall"
            data-testid="StartButton-upload"
            className="ndx-Welcome_Lg-buttonText">
            Upload
          </Typography>
        </div>
        <div
          className="ndx-Welcome_Lg-button"
          data-testid="onAddText"
          onClick={onAddText}>
          <div className="ndx-Welcome_Lg-buttonImage welcomeTextCTAImage" />
          <Typography
            variant="uiTextSmall"
            className="ndx-Welcome_Lg-buttonText">
            Add Text
          </Typography>
        </div>
      </div>
      <div className="ndx-Flex-Row">
        <div className="ndx-Welcome_Lg-button" onClick={onAddArt}>
          <div className="ndx-Welcome_Lg-buttonImage welcomeArtCTAImage" />
          <Typography
            variant="uiTextSmall"
            className="ndx-Welcome_Lg-buttonText">
            {'Add Art'}
          </Typography>
        </div>
        <div className="ndx-Welcome_Lg-button" onClick={onProduct}>
          <div className="ndx-Welcome_Lg-buttonImage welcomeProductCTAImage" />
          <Typography
            variant="uiTextSmall"
            className="ndx-Welcome_Lg-buttonText">
            Change
            <br />
            Products
          </Typography>
        </div>
      </div>
      {isMultiItemEligible && (
        <div className="ndx-Flex-Row">
          {auth.user && (
            <RetrieveDesignsButton onRetrieveDesigns={onRetrieveDesigns} />
          )}
          {cartItemCount > 0 && (
            <ReviewCartButton onCheckout={onCheckoutConfirmation} />
          )}
        </div>
      )}
      <DesignUnsavedDialog
        open={dialogOpen}
        subtitle="You will lose any unsaved changes by continuing to your cart."
        onSubmit={handleSubmit}
        onDismiss={handleDismiss}
        onClose={handleCloseDialog}
      />
    </div>
  );
};

export default StartButtons;
